import { type Order } from '@components/account/types'
import { formatDate } from '@helpers/utils'

const CANCEL_REASON_LABELS: Record<
  NonNullable<Order['cancelReason']>,
  string
> = {
  CUSTOMER: 'Customer request',
  DECLINED: 'Payment declined',
  FRAUD: 'Order cancelled',
  INVENTORY: 'Out of stock',
  STAFF: 'Staff error',
  OTHER: 'Order cancelled'
}

export type OrderStatusProps = {
  status:
    | 'Cancelled'
    | 'In Transit'
    | 'Partial Delivery'
    | 'Oops'
    | 'In Progress'
    | 'Refunded'
    | 'Shipped'
  label: string
}

const getFulfilledVariantIds = (
  fulfillments: Order['fulfillments']
): Set<string> => {
  const ids = new Set<string>()
  fulfillments.forEach(fulfillment => {
    fulfillment.fulfillmentLineItems.forEach(({ lineItem }) => {
      if (lineItem.variantId) ids.add(lineItem.variantId)
    })
  })
  return ids
}

const getActiveTrackingUrl = (
  fulfillments: Order['fulfillments'],
  lineItems: Order['lineItems']
): string | undefined | null => {
  const activeVariantIds = new Set(
    lineItems
      .filter(
        item => (item.refundableQuantity ?? 0) > 0 && item.variantId != null
      )
      .map(item => item.variantId!)
  )

  return fulfillments
    .filter(fulfillment =>
      fulfillment.fulfillmentLineItems.some(
        ({ lineItem }) =>
          lineItem.variantId && activeVariantIds.has(lineItem.variantId)
      )
    )
    .flatMap(f => f.trackingInformation ?? [])
    .find(t => t?.url)?.url
}

export function getOrderStatus(
  fulfillments: Order['fulfillments'],
  lineItems: Order['lineItems'],
  cancelledAt?: Order['cancelledAt'],
  refunds: Order['refunds'] = [],
  cancelReason?: Order['cancelReason']
): OrderStatusProps {
  if (cancelledAt) {
    return {
      status: 'Cancelled',
      label: cancelReason
        ? CANCEL_REASON_LABELS[cancelReason]
        : `on ${formatDate(cancelledAt)}`
    }
  }

  if (fulfillments.length === 0) {
    return {
      status: 'In Progress',
      label: 'We are on it'
    }
  }

  const trackingUrl = getActiveTrackingUrl(fulfillments, lineItems)

  const fulfilledVariantIds = getFulfilledVariantIds(fulfillments)
  const hasUnfulfilledItems = lineItems.some(
    item =>
      item.variantId != null &&
      !fulfilledVariantIds.has(item.variantId)
  )

  if (hasUnfulfilledItems) {
    return {
      status: 'Partial Delivery',
      label: trackingUrl ? 'Tracking Added' : 'Delivery in Progress'
    }
  }

  if (refunds.length) {
    const hasUnrefundedItems = lineItems.some(
      item => item.refundableQuantity > 0
    )

    if (hasUnrefundedItems) {
      return {
        status: 'Shipped',
        label: 'Partial Return'
      }
    }

    return {
      status: 'Refunded',
      label: `on ${formatDate(refunds[refunds.length - 1].createdAt)}`
    }
  }

  if (trackingUrl) {
    return {
      status: 'Shipped',
      label: 'Tracking Added'
    }
  }

  return {
    status: 'In Transit',
    label: 'Delivery in Progress'
  }
}
